import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http'
import { inject } from '@angular/core'
import { Router } from '@angular/router'
import { SystemStatus } from '@onedv-portal/api'
import { catchError, from, NEVER, switchMap, throwError } from 'rxjs'
import { environment } from '../../environments/environment'

/** sends the user to the maintenance page as soon as the api answers a request with the maintenance 503 */
export const maintenanceInterceptor: HttpInterceptorFn = (req, next) => {
  const router = inject(Router)

  return next(req).pipe(
    catchError((error: unknown) => {
      if (!req.url.startsWith(environment.apiUrl) || !isMaintenanceResponse(error)) {
        return throwError(() => error)
      }
      // a failed write keeps its error so the caller still tells the user the change was not saved
      if (req.method !== 'GET') {
        void toMaintenancePage(router)
        return throwError(() => error)
      }
      // a read ends silently but gets its error back if the unsaved changes dialog cancels the navigation
      return from(toMaintenancePage(router)).pipe(
        switchMap((navigated) => (navigated ? NEVER : throwError(() => error)))
      )
    })
  )
}

// resolves to true when the user is on the maintenance page or on the way there
const toMaintenancePage = (router: Router): Promise<boolean> => {
  // a 503 from a resolver returns the user to the target of that navigation
  const target = router.currentNavigation()?.extractedUrl
  const returnUrl = target ? router.serializeUrl(target) : router.url
  // when parallel requests fail only the first one navigates
  if (returnUrl.startsWith('/maintenance')) {
    return Promise.resolve(true)
  }
  return router.navigate(['/maintenance'], { queryParams: { returnUrl } })
}

// api gateway also sends its own 503 on timeouts but only the backend gate adds a status
const isMaintenanceResponse = (error: unknown): boolean =>
  error instanceof HttpErrorResponse && error.status === 503 && SystemStatus.safeParse(error.error?.status).success
